import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkle, Phone, Mail, MapPin, ArrowRight, Loader2, Linkedin } from "lucide-react";
import { useSiteContent, txt } from "../hooks/useSiteContent";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const services = [
  "PR, Media & Influencer Marketing",
  "Influencer Marketing",
  "Strategic Partnerships",
  "Digital Business & Product Launches",
  "NYC Event Experiences & Brand Activations",
  "Growth Marketing · Lead Gen · AI Consulting",
  "Branding & Digital Marketing",
  "Not sure yet — let's talk",
];

const budgets = [
  "< $10k / month",
  "$10k – $25k / month",
  "$25k – $50k / month",
  "$50k+ / month",
  "Project-based",
];

export default function Contact() {
  const content = useSiteContent();
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    service: services[0],
    budget: budgets[0],
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setSubmitted(true);
      toast.success("Request received. Denisse will be in touch within 48 hours.");
      setForm({
        name: "",
        email: "",
        company: "",
        service: services[0],
        budget: budgets[0],
        message: "",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please email denisse@novamediapr.com.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#050505] py-16 md:py-24 lg:py-32 grain"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-6 md:gap-12">
          {/* Left — invitation */}
          <div className="col-span-12 md:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="text-[10px] uppercase tracking-[0.35em] text-[#7FB3D5]">
                ✦ Let&apos;s connect
              </span>
              <span className="text-[10px] uppercase tracking-[0.3em] text-white/40">
                Page 09
              </span>
            </div>
            <h2
              data-testid="contact-headline"
              className="font-display text-4xl md:text-6xl text-white leading-[0.95] tracking-tight font-light"
            >
              {txt(
                content,
                "contact.headline",
                "Let's build the visibility your brand deserves."
              ) === "Let's build the visibility your brand deserves." ? (
                <>
                  Let&apos;s build the
                  <span className="italic text-[#7FB3D5]"> visibility </span>
                  your brand deserves.
                </>
              ) : (
                txt(content, "contact.headline", "Let's build the visibility your brand deserves.")
              )}
            </h2>
            <p className="mt-8 text-base md:text-lg text-white/70 leading-relaxed max-w-md">
              Whether you are preparing for a launch, strengthening your
              authority, expanding into new markets, or ready to become more
              visible, NOVA Media PR can help you create a strategic path
              forward.
            </p>

            <div className="mt-12 space-y-5">
              <a
                href="tel:+19302055843"
                data-testid="contact-phone"
                className="group flex items-center gap-4 text-white/80 hover:text-[#7FB3D5] transition-colors duration-300"
              >
                <span className="w-10 h-10 border border-[#7FB3D5]/30 flex items-center justify-center group-hover:border-[#7FB3D5]">
                  <Phone className="w-4 h-4 text-[#7FB3D5]" strokeWidth={1.2} />
                </span>
                <span className="text-base font-light tracking-wide">
                  +1 (930) 205-5843
                </span>
              </a>
              <a
                href="mailto:denisse@novamediapr.com"
                data-testid="contact-email"
                className="group flex items-center gap-4 text-white/80 hover:text-[#7FB3D5] transition-colors duration-300"
              >
                <span className="w-10 h-10 border border-[#7FB3D5]/30 flex items-center justify-center group-hover:border-[#7FB3D5]">
                  <Mail className="w-4 h-4 text-[#7FB3D5]" strokeWidth={1.2} />
                </span>
                <span className="text-base font-light tracking-wide">
                  denisse@novamediapr.com
                </span>
              </a>
              <a
                href="https://www.linkedin.com/in/denissetroconisaoun"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-linkedin"
                className="group flex items-center gap-4 text-white/80 hover:text-[#7FB3D5] transition-colors duration-300"
              >
                <span className="w-10 h-10 border border-[#7FB3D5]/30 flex items-center justify-center group-hover:border-[#7FB3D5]">
                  <Linkedin className="w-4 h-4 text-[#7FB3D5]" strokeWidth={1.2} />
                </span>
                <span className="text-base font-light tracking-wide">
                  /in/denissetroconisaoun
                </span>
              </a>
              <div
                data-testid="contact-address"
                className="flex items-start gap-4 text-white/80"
              >
                <span className="w-10 h-10 border border-[#7FB3D5]/30 flex items-center justify-center mt-0.5">
                  <MapPin className="w-4 h-4 text-[#7FB3D5]" strokeWidth={1.2} />
                </span>
                <span className="text-base font-light tracking-wide leading-relaxed">
                  26 Broadway
                  <br />
                  New York, NY 10004
                </span>
              </div>
            </div>
          </div>

          {/* Right — form */}
          <div className="col-span-12 md:col-span-7 md:pl-8 lg:pl-16">
            {submitted ? (
              <div
                data-testid="contact-success-state"
                className="border border-[#7FB3D5]/40 bg-[#080808] p-10 md:p-14 text-center min-h-[500px] flex flex-col items-center justify-center"
              >
                <Sparkle
                  className="w-10 h-10 text-[#7FB3D5] mb-6"
                  fill="#7FB3D5"
                  strokeWidth={0.6}
                />
                <h3 className="font-display text-3xl md:text-4xl text-white font-light">
                  Beautifully <span className="italic text-[#7FB3D5]">received.</span>
                </h3>
                <p className="mt-6 text-white/70 max-w-md leading-relaxed">
                  Your request is on Denisse&apos;s desk. Expect a personal
                  reply within 48 hours — we&apos;re selective, and that means
                  we read everything.
                </p>
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  data-testid="contact-send-another"
                  className="mt-10 text-[10px] uppercase tracking-[0.3em] text-[#7FB3D5] border-b border-[#7FB3D5]/60 pb-1 hover:border-white hover:text-white transition-colors duration-300"
                >
                  Send another note
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="border border-white/10 bg-[#080808] p-8 md:p-12 space-y-7"
              >
                <Field
                  label="Full name"
                  testid="contact-input-name"
                  value={form.name}
                  onChange={update("name")}
                  required
                  type="text"
                  placeholder="Your name"
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                  <Field
                    label="Email"
                    testid="contact-input-email"
                    value={form.email}
                    onChange={update("email")}
                    required
                    type="email"
                    placeholder="you@brand.com"
                  />
                  <Field
                    label="Company / Brand"
                    testid="contact-input-company"
                    value={form.company}
                    onChange={update("company")}
                    type="text"
                    placeholder="Optional"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                  <SelectField
                    label="Service interest"
                    testid="contact-input-service"
                    value={form.service}
                    onChange={update("service")}
                    options={services}
                  />
                  <SelectField
                    label="Investment range"
                    testid="contact-input-budget"
                    value={form.budget}
                    onChange={update("budget")}
                    options={budgets}
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.3em] text-white/50 block mb-3">
                    Tell us about your brand &amp; moment
                  </label>
                  <textarea
                    data-testid="contact-input-message"
                    value={form.message}
                    onChange={update("message")}
                    required
                    rows={5}
                    placeholder="A few lines about your business, what you're launching, and the rooms you'd like to be in..."
                    className="w-full bg-transparent text-white placeholder:text-white/30 border-b border-white/20 focus:border-[#7FB3D5] outline-none pb-3 font-light text-base resize-none transition-colors duration-300"
                  />
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4">
                  <p className="text-[10px] uppercase tracking-[0.28em] text-white/40">
                    ✦ Replies within 48 hours
                  </p>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-form-submit"
                    className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#7FB3D5] text-black text-[11px] uppercase tracking-[0.3em] hover:bg-white transition-colors duration-500 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending
                      </>
                    ) : (
                      <>
                        Send request
                        <ArrowRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.3em] text-white/50 block mb-3">
        {label}
      </label>
      <input
        data-testid={testid}
        {...props}
        className="w-full bg-transparent text-white placeholder:text-white/30 border-b border-white/20 focus:border-[#7FB3D5] outline-none pb-3 font-light text-base transition-colors duration-300"
      />
    </div>
  );
}

function SelectField({ label, testid, options, ...props }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.3em] text-white/50 block mb-3">
        {label}
      </label>
      <div className="relative">
        <select
          data-testid={testid}
          {...props}
          className="w-full appearance-none bg-transparent text-white border-b border-white/20 focus:border-[#7FB3D5] outline-none pb-3 pr-6 font-light text-base transition-colors duration-300"
        >
          {options.map((o) => (
            <option key={o} value={o} className="bg-[#080808] text-white">
              {o}
            </option>
          ))}
        </select>
        <span className="absolute right-0 bottom-3 text-[#7FB3D5] pointer-events-none">
          ✦
        </span>
      </div>
    </div>
  );
}
